/**
 * EmbedBlock — Embed external content (YouTube, Figma, Loom, Google Docs, etc.)
 * Uses content: 'none' pattern like MermaidBlock.
 */

import { useState, useRef, useEffect } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { ExternalLink, Pencil, Globe } from 'lucide-react';

// ── Provider detection ────────────────────────────────────────────────────

interface EmbedProvider {
  name: string;
  icon: string;
  aspectRatio: string; // CSS aspect-ratio value
  transform: (url: string) => string; // Convert to embeddable URL
}

const PROVIDERS: { test: RegExp; provider: EmbedProvider }[] = [
  {
    test: /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
    provider: {
      name: 'YouTube',
      icon: '\u{1F3AC}',
      aspectRatio: '16 / 9',
      transform: (url) => {
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
        return match ? `https://www.youtube.com/embed/${match[1]}` : url;
      },
    },
  },
  {
    test: /figma\.com\/(design|file|proto|board)\//,
    provider: {
      name: 'Figma',
      icon: '\u{1F3A8}',
      aspectRatio: '16 / 10',
      transform: (url) => `https://www.figma.com/embed?embed_host=martha&url=${encodeURIComponent(url)}`,
    },
  },
  {
    test: /loom\.com\/(share|embed)\//,
    provider: {
      name: 'Loom',
      icon: '\u{1F4F9}',
      aspectRatio: '16 / 9',
      transform: (url) => url.replace('/share/', '/embed/'),
    },
  },
  {
    test: /docs\.google\.com\/(document|spreadsheets|presentation)/,
    provider: {
      name: 'Google Docs',
      icon: '\u{1F4C4}',
      aspectRatio: '4 / 3',
      transform: (url) => {
        if (url.includes('/pub')) return url;
        return url.replace(/\/edit.*$/, '/preview');
      },
    },
  },
  {
    test: /vimeo\.com\/(\d+)/,
    provider: {
      name: 'Vimeo',
      icon: '\u{1F3AC}',
      aspectRatio: '16 / 9',
      transform: (url) => {
        const match = url.match(/vimeo\.com\/(\d+)/);
        return match ? `https://player.vimeo.com/video/${match[1]}` : url;
      },
    },
  },
];

function detectProvider(url: string): EmbedProvider {
  for (const { test, provider } of PROVIDERS) {
    if (test.test(url)) return provider;
  }
  return {
    name: 'Website',
    icon: '\u{1F310}',
    aspectRatio: '16 / 10',
    transform: (u) => u,
  };
}

// ── Renderer ──────────────────────────────────────────────────────────────

interface EmbedBlockRendererProps {
  block: {
    id: string;
    props: { url: string };
  };
  editor: {
    updateBlock: (id: string, update: { props: Partial<{ url: string }> }) => void;
    isEditable: boolean;
  };
}

function EmbedBlockRenderer({ block, editor }: EmbedBlockRendererProps) {
  const { url } = block.props;
  const [inputUrl, setInputUrl] = useState(url || '');
  const [isEditing, setIsEditing] = useState(!url);
  const inputRef = useRef<HTMLInputElement>(null);
  const isEditable = editor.isEditable;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSubmit = () => {
    const trimmed = inputUrl.trim();
    if (!trimmed) return;
    editor.updateBlock(block.id, { props: { url: trimmed } });
    setIsEditing(false);
  };

  // Empty state — URL input
  if (!url || isEditing) {
    return (
      <div
        className="embed-block-input flex items-center gap-2 px-4 py-3 rounded-lg border border-dashed border-stone-300 bg-stone-50 my-1"
        contentEditable={false}
      >
        <Globe className="w-4 h-4 text-stone-400 shrink-0" />
        <input
          ref={inputRef}
          type="url"
          value={inputUrl}
          onChange={(e) => setInputUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') {
              if (url) {
                setInputUrl(url);
                setIsEditing(false);
              }
            }
          }}
          placeholder="Paste a URL (YouTube, Figma, Loom, Google Docs, or any website)..."
          className="flex-1 text-sm bg-transparent border-none outline-none placeholder-stone-400"
        />
        <button
          onClick={handleSubmit}
          disabled={!inputUrl.trim()}
          className="px-3 py-1 text-xs font-medium bg-stone-800 text-white rounded-md
                     hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Embed
        </button>
        {url && (
          <button
            onClick={() => { setInputUrl(url); setIsEditing(false); }}
            className="px-2 py-1 text-xs text-stone-500 hover:text-stone-700"
          >
            Cancel
          </button>
        )}
      </div>
    );
  }

  // Rendered embed
  const provider = detectProvider(url);
  const embedUrl = provider.transform(url);

  return (
    <div className="embed-block my-2 rounded-lg overflow-hidden border border-stone-200" contentEditable={false}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-stone-50 border-b border-stone-200 text-xs text-stone-500">
        <span>{provider.icon}</span>
        <span className="font-medium">{provider.name}</span>
        <span className="truncate flex-1 text-stone-400">{url}</span>
        {isEditable && (
          <button
            onClick={() => setIsEditing(true)}
            className="p-1 hover:text-stone-700 transition-colors"
            title="Edit URL"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 hover:text-stone-700 transition-colors"
          title="Open in new tab"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Iframe */}
      <div style={{ aspectRatio: provider.aspectRatio }}>
        <iframe
          src={embedUrl}
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
          title={`${provider.name} embed`}
        />
      </div>
    </div>
  );
}

// ── Block spec ────────────────────────────────────────────────────────────

export const embedBlockSpec = createReactBlockSpec(
  {
    type: 'embed' as const,
    propSchema: {
      url: { default: '' },
    },
    content: 'none',
  },
  {
    render: (props) => {
      const blockProps: EmbedBlockRendererProps = {
        block: {
          id: props.block.id,
          props: props.block.props as { url: string },
        },
        editor: {
          updateBlock: (id, update) => (props.editor as any).updateBlock(id, update),
          isEditable: (props.editor as any).isEditable,
        },
      };
      return <EmbedBlockRenderer {...blockProps} />;
    },
  }
);

export default embedBlockSpec;
